import * as PIXI from "pixi.js";

/**
 * LRU tile texture cache.
 *
 * Manages loaded tile textures with a configurable capacity.
 * When the cache exceeds capacity, the least-recently-used tiles
 * are evicted and their textures destroyed.
 */

export interface TileKey {
  z: number;
  x: number;
  y: number;
}

export function tileKeyStr(z: number, x: number, y: number): string {
  return `${z}:${x}:${y}`;
}

interface CacheEntry {
  key: string;
  texture: PIXI.Texture;
}

export class TileCache {
  private _capacity: number;
  private _map = new Map<string, CacheEntry>();
  private _order: string[] = []; // most recent at end

  constructor(capacity: number) {
    this._capacity = capacity;
  }

  get size(): number {
    return this._map.size;
  }

  has(key: string): boolean {
    return this._map.has(key);
  }

  get(key: string): PIXI.Texture | null {
    const entry = this._map.get(key);
    if (!entry) return null;

    // Move to end (most recently used)
    const idx = this._order.indexOf(key);
    if (idx !== -1) {
      this._order.splice(idx, 1);
    }
    this._order.push(key);

    return entry.texture;
  }

  set(key: string, texture: PIXI.Texture): void {
    if (this._map.has(key)) {
      // Update existing - move to end
      const idx = this._order.indexOf(key);
      if (idx !== -1) this._order.splice(idx, 1);
      this._order.push(key);
      this._map.get(key)!.texture = texture;
      return;
    }

    // Evict if at capacity
    while (this._map.size >= this._capacity && this._order.length > 0) {
      const evictKey = this._order.shift()!;
      const evicted = this._map.get(evictKey);
      if (evicted) {
        evicted.texture.destroy(true);
        this._map.delete(evictKey);
      }
    }

    this._map.set(key, { key, texture });
    this._order.push(key);
  }

  /** Remove a specific tile from the cache. */
  delete(key: string): void {
    const entry = this._map.get(key);
    if (!entry) return;
    entry.texture.destroy(true);
    this._map.delete(key);
    const idx = this._order.indexOf(key);
    if (idx !== -1) this._order.splice(idx, 1);
  }

  /** Destroy all cached textures and clear the cache. */
  clear(): void {
    for (const entry of this._map.values()) {
      entry.texture.destroy(true);
    }
    this._map.clear();
    this._order = [];
  }
}
